"use client";

import BeeIcon from "@/components/icons/BeeIcon";
import DizzyBeeIcon from "@/components/icons/DizzyBeeIcon";
import SectionContainer from "@/components/marketing/SectionContainer";
import { Send } from "@mui/icons-material";
import {
  Alert,
  Box,
  Button,
  FormControl,
  FormHelperText,
  InputLabel,
  MenuItem,
  Select,
  TextField,
  Typography,
} from "@mui/material";
import Grid from "@mui/material/Grid2";
import Link from "next/link";
import { useSearchParams } from "next/navigation";
import { Suspense, useEffect, useState } from "react";
import { useForm } from "react-hook-form";

function ContactFormContent() {
  const [submitted, setSubmitted] = useState(false);
  const [loading, setLoading] = useState(false);
  const [locked, setLocked] = useState(false);
  const [error, setError] = useState(null);
  const {
    register,
    handleSubmit,
    watch,
    reset,
    setValue,
    formState: { errors },
  } = useForm({
    defaultValues: {
      topic: "",
      name: "",
      email: "",
      message: "",
      neighborhood: "",
      groupSize: "",
    },
  });

  const selectedTopic = watch("topic");
  const searchParams = useSearchParams();

  useEffect(() => {
    const topicParam = searchParams.get("topic");
    if (topicParam) {
      setValue("topic", topicParam);
    }
  }, [searchParams, setValue]);

  const onSubmit = async (data) => {
    setLoading(true);

    try {
      const apiUrl =
        process.env.NEXT_PUBLIC_API_URL?.replace(/\/$/, "") ||
        "http://localhost:5000/api";
      const res = await fetch(`${apiUrl}/email/contact`, {
        method: "POST",
        headers: {
          "Content-Type": "application/json",
        },
        body: JSON.stringify(data),
      });

      if (!res.ok) {
        const errorData = await res.json().catch(() => null);
        if (res.status === 429) {
          setLocked(true);
        }
        throw new Error(errorData?.error || "Failed to send message");
      }

      setError(null);
      setSubmitted(true);
    } catch (err) {
      console.error("Submission error:", err);
      setError(
        err.message ||
          "Something went wrong. Please try again or email us directly at info@sunhivemicroschool.org.",
      );
    } finally {
      setLoading(false);
    }
  };

  if (submitted) {
    return (
      <SectionContainer
        id="contact-form"
        backgroundColor="background.default"
        py={{ xs: 8, md: 10 }}
      >
        <Box sx={{ textAlign: "center", maxWidth: "500px", mx: "auto" }}>
          <Box
            sx={{
              fontSize: { xs: "6rem", md: "8rem" },
              mb: 2,
              lineHeight: 1,
              "@keyframes sway": {
                "0%, 100%": {
                  transform: "rotate(-5deg)",
                },
                "50%": {
                  transform: "rotate(5deg)",
                },
              },
              animation: "sway 2.5s ease-in-out infinite",
            }}
          >
            <BeeIcon sx={{ fontSize: "inherit" }} />
          </Box>

          <Typography
            variant="h4"
            sx={{
              fontWeight: 600,
              mb: 2,
              color: "text.primary",
            }}
          >
            Message Sent!
          </Typography>

          <Typography
            sx={{
              color: "text.secondary",
              mb: 4,
              fontSize: "1.1rem",
              lineHeight: 1.7,
            }}
          >
            Our busy bee has delivered your message.
            <br />
            We&apos;ll be in touch soon!
          </Typography>

          <Button
            variant="outlined"
            onClick={() => {
              reset();
              setSubmitted(false);
            }}
            sx={{
              borderColor: "primary.main",
              color: "primary.main",
              fontWeight: 600,
              borderRadius: "8px",
              px: 3,
              py: 1,
              "&:hover": {
                backgroundColor: "primary.main",
                color: "primary.contrastText",
              },
            }}
          >
            Send Another Message
          </Button>
        </Box>
      </SectionContainer>
    );
  }

  if (locked) {
    return (
      <SectionContainer
        id="contact-form"
        backgroundColor="background.default"
        py={{ xs: 8, md: 10 }}
      >
        <Box sx={{ textAlign: "center", maxWidth: "500px", mx: "auto" }}>
          <DizzyBeeIcon sx={{ fontSize: { xs: "6rem", md: "8rem" }, mb: 3 }} />

          <Typography
            variant="h4"
            sx={{
              fontWeight: 600,
              mb: 2,
              color: "text.primary",
            }}
          >
            Whoa there!
          </Typography>

          <Typography
            sx={{
              color: "text.secondary",
              mb: 3,
              fontSize: "1.1rem",
              lineHeight: 1.7,
            }}
          >
            Our busy bee can&apos;t keep up with all your messages!
            <br />
            You can use this contact form again after 24 hours.
          </Typography>

          <Typography
            sx={{
              color: "text.secondary",
              fontSize: "0.95rem",
            }}
          >
            Need to reach us sooner? Email us directly at{" "}
            <Link
              href="mailto:info@sunhivemicroschool.org"
              style={{ color: "inherit", fontWeight: 600 }}
            >
              info@sunhivemicroschool.org
            </Link>
          </Typography>
        </Box>
      </SectionContainer>
    );
  }

  return (
    <SectionContainer
      id="contact-form"
      backgroundColor="background.default"
      py={{ xs: 8, md: 10 }}
    >
      <Box sx={{ maxWidth: "600px", mx: "auto", minHeight: "500px" }}>
        <Typography
          variant="h2"
          component="h2"
          sx={{
            fontSize: { xs: "1.75rem", md: "2.25rem" },
            fontWeight: 600,
            textAlign: "center",
            mb: 4,
          }}
        >
          Send Us a Message
        </Typography>

        <Box component="form" onSubmit={handleSubmit(onSubmit)}>
          {error && (
            <Alert severity="error" sx={{ mb: 3, borderRadius: "8px" }}>
              {error}
            </Alert>
          )}

          <FormControl fullWidth sx={{ mb: 3 }}>
            <InputLabel id="topic-label">Topic</InputLabel>
            <Select
              labelId="topic-label"
              label="Topic"
              value={selectedTopic || ""}
              {...register("topic", { required: "Please select a topic" })}
              error={!!errors.topic}
              sx={{ backgroundColor: "background.paper" }}
            >
              <MenuItem value="general">General Inquiry</MenuItem>
              <MenuItem value="admissions">Admissions Question</MenuItem>
              <MenuItem value="host">Host a Session</MenuItem>
              <MenuItem value="volunteer">Volunteer</MenuItem>
            </Select>
            {errors.topic && (
              <FormHelperText error>{errors.topic.message}</FormHelperText>
            )}
          </FormControl>

          {selectedTopic === "admissions" && (
            <Alert severity="info" sx={{ mb: 3 }}>
              Have you checked our{" "}
              <Link
                href="/admissions#faq"
                style={{ color: "inherit", fontWeight: 600 }}
              >
                Admissions page
              </Link>{" "}
              or{" "}
              <Link href="#faq" style={{ color: "inherit", fontWeight: 600 }}>
                Tuition FAQ
              </Link>{" "}
              below?
            </Alert>
          )}

          {selectedTopic === "host" && (
            <Grid container spacing={2} sx={{ mb: 3 }}>
              <Grid size={{ xs: 12, sm: 6 }}>
                <TextField
                  fullWidth
                  label="Neighborhood / Zip Code"
                  {...register("neighborhood", {
                    required:
                      selectedTopic === "host"
                        ? "Please enter your neighborhood"
                        : false,
                  })}
                  error={!!errors.neighborhood}
                  helperText={errors.neighborhood?.message}
                  sx={{ backgroundColor: "background.paper" }}
                />
              </Grid>
              <Grid size={{ xs: 12, sm: 6 }}>
                <TextField
                  fullWidth
                  label="Estimated Group Size"
                  type="number"
                  {...register("groupSize", {
                    required:
                      selectedTopic === "host"
                        ? "Please estimate group size"
                        : false,
                  })}
                  error={!!errors.groupSize}
                  helperText={errors.groupSize?.message}
                  sx={{ backgroundColor: "background.paper" }}
                />
              </Grid>
            </Grid>
          )}

          <TextField
            fullWidth
            label="Your Name"
            {...register("name", { required: "Please enter your name" })}
            error={!!errors.name}
            helperText={errors.name?.message}
            sx={{ mb: 3, backgroundColor: "background.paper" }}
          />

          <TextField
            fullWidth
            label="Email Address"
            type="email"
            {...register("email", {
              required: "Please enter your email",
              pattern: {
                value: /^[^\s@]+@[^\s@]+\.[^\s@]+$/,
                message: "Please enter a valid email address",
              },
            })}
            error={!!errors.email}
            helperText={errors.email?.message}
            sx={{ mb: 3, backgroundColor: "background.paper" }}
          />

          <TextField
            fullWidth
            label="Message"
            multiline
            rows={4}
            {...register("message", { required: "Please enter a message" })}
            error={!!errors.message}
            helperText={errors.message?.message}
            sx={{ mb: 3, backgroundColor: "background.paper" }}
          />

          <Button
            type="submit"
            variant="contained"
            size="large"
            disabled={loading || locked}
            endIcon={<Send />}
            sx={{
              backgroundColor: "primary.main",
              color: "secondary.main",
              fontWeight: 600,
              px: 4,
              py: 1.5,
              borderRadius: "8px",
              width: "100%",
              "&:hover": {
                backgroundColor: "primary.light",
              },
            }}
          >
            Send Message
          </Button>
        </Box>
      </Box>
    </SectionContainer>
  );
}

// Fallback component - renders the form without search params
function ContactFormFallback() {
  return (
    <SectionContainer
      id="contact-form"
      backgroundColor="background.default"
      py={{ xs: 8, md: 10 }}
    >
      <Box sx={{ maxWidth: "600px", mx: "auto", minHeight: "500px" }}>
        <Typography
          variant="h2"
          component="h2"
          sx={{
            fontSize: { xs: "1.75rem", md: "2.25rem" },
            fontWeight: 600,
            textAlign: "center",
            mb: 4,
          }}
        >
          Send Us a Message
        </Typography>

        <Box component="form">
          <FormControl fullWidth sx={{ mb: 3 }}>
            <InputLabel id="topic-label">Topic</InputLabel>
            <Select
              labelId="topic-label"
              label="Topic"
              value=""
              disabled
              sx={{ backgroundColor: "background.paper" }}
            >
              <MenuItem value="general">General Inquiry</MenuItem>
              <MenuItem value="admissions">Admissions Question</MenuItem>
              <MenuItem value="host">Host a Session</MenuItem>
              <MenuItem value="volunteer">Volunteer</MenuItem>
            </Select>
          </FormControl>

          <TextField
            fullWidth
            label="Your Name"
            disabled
            sx={{ mb: 3, backgroundColor: "background.paper" }}
          />

          <TextField
            fullWidth
            label="Email Address"
            type="email"
            disabled
            sx={{ mb: 3, backgroundColor: "background.paper" }}
          />

          <TextField
            fullWidth
            label="Message"
            multiline
            rows={4}
            disabled
            sx={{ mb: 3, backgroundColor: "background.paper" }}
          />

          <Button
            variant="contained"
            size="large"
            disabled
            endIcon={<Send />}
            sx={{
              backgroundColor: "primary.main",
              color: "secondary.main",
              fontWeight: 600,
              px: 4,
              py: 1.5,
              borderRadius: "8px",
              width: "100%",
            }}
          >
            Send Message
          </Button>
        </Box>
      </Box>
    </SectionContainer>
  );
}

export default function ContactForm() {
  return (
    <Suspense fallback={<ContactFormFallback />}>
      <ContactFormContent />
    </Suspense>
  );
}
